import { ButtonProps } from "@mantine/core";
import { useCallback, useMemo } from "react";
import McDropdownNew from "./MCDropdownNew";

interface ILegacyFilterOption<T> extends Record<string, unknown> {
  label: string;
  value: T;
  isActive?: boolean;
}

interface ILegacyBaseProps {
  color?: string;
  disabled?: boolean;
  withFilter?: boolean;
  tooltip?: string;
  menuWidth?: number;
  withinPortal?: boolean;
  variant?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  justify?: "flex-start" | "center" | "flex-end" | "space-between";
  withSelectAll?: boolean;
  menuMaxHeight?: number;
  buttonProps?: Omit<ButtonProps, "children">;
}

export type IMcDropdownLegacy<T> = ILegacyBaseProps & {
  filterOptions: ILegacyFilterOption<T>[];
  onClick?: (value: T) => void;
  title?: string;
  enableSearch?: boolean;
  selectAll?: () => void;
  unSelectAll?: () => void;
  textColor?: string;
};

function McDropdownLegacy<T>({
  filterOptions,
  onClick,
  title,
  enableSearch,
  selectAll,
  unSelectAll,
  color,
  disabled,
  tooltip,
  menuWidth,
  withinPortal,
  variant,
  size,
  justify,
  menuMaxHeight,
  buttonProps,
}: IMcDropdownLegacy<T>) {
  const sortedOptions = useMemo(
    () =>
      [...(filterOptions ?? [])]
        .filter((option) => option?.label != null && option?.value != null)
        .sort((a, b) => String(a.label).localeCompare(String(b.label), "nb")),
    [filterOptions],
  );

  const selectedOptions = useMemo(() => sortedOptions.filter((option) => option.isActive === true), [sortedOptions]);

  const selectedIds = useMemo(() => selectedOptions.map((option) => String(option.value)), [selectedOptions]);

  const hasSelectAllControls = typeof unSelectAll === "function" || typeof selectAll === "function";

  const normalizedButtonProps = useMemo(
    () => ({
      fullWidth: false,
      ...buttonProps,
      style: {
        alignSelf: "flex-start",
        width: "fit-content",
        ...buttonProps?.style,
      },
    }),
    [buttonProps],
  );

  const handleLegacyMultiSelectChange = useCallback(
    (nextSelected: ILegacyFilterOption<T>[]) => {
      const nextIds = new Set(nextSelected.map((option) => String(option.value)));
      const prevIds = new Set(selectedIds);

      if (typeof unSelectAll === "function" && nextSelected.length === 0 && selectedIds.length > 0) {
        unSelectAll();
        return;
      }

      if (
        typeof selectAll === "function" &&
        sortedOptions.length > 0 &&
        nextSelected.length === sortedOptions.length &&
        selectedIds.length !== sortedOptions.length
      ) {
        selectAll();
        return;
      }

      sortedOptions.forEach((option) => {
        const id = String(option.value);
        if (nextIds.has(id) !== prevIds.has(id)) {
          onClick?.(option.value);
        }
      });
    },
    [onClick, selectAll, selectedIds, sortedOptions, unSelectAll],
  );

  return (
    <McDropdownNew<ILegacyFilterOption<T>>
      currentValue={null as unknown as ILegacyFilterOption<T>}
      onChange={() => {
        // Legacy filter props rely on onMultiSelectChange/onClick.
      }}
      options={sortedOptions}
      displayKey="label"
      selectedDisplayKey="value"
      defaultTitle={title}
      color={color}
      disabled={disabled}
      withFilter={enableSearch !== false}
      tooltip={tooltip}
      menuWidth={menuWidth}
      withinPortal={withinPortal}
      variant={variant}
      size={size}
      justify={justify}
      menuMaxHeight={menuMaxHeight}
      buttonProps={normalizedButtonProps}
      multiSelect
      selectedValues={selectedOptions}
      onMultiSelectChange={handleLegacyMultiSelectChange}
      showSelectionBadge
      withSelectAll={hasSelectAllControls}
    />
  );
}

export default McDropdownLegacy;
